import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useLang } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";

export default function ProjectPage() {
  const { id } = useParams();
  const { content, L } = useContent();
  const { t } = useLang();
  const project = content?.projects?.find((p) => p.id === id && !p.demo);

  useEffect(() => {
    if (project) document.title = `${project.title} — Bezalel Visionary`;
    window.scrollTo(0, 0);
  }, [project]);

  if (!content) {
    return <div className="min-h-screen bg-[#F7F3EC] flex items-center justify-center text-[#8A8378]">{t.misc.loading}</div>;
  }

  if (!project) {
    return (
      <main data-testid="project-not-found">
        <Navbar />
        <div className="min-h-screen bg-[#F7F3EC] flex flex-col items-center justify-center gap-6 px-6">
          <p className="text-[#191713]/60">—</p>
          <Link to="/#work" className="btn-dark">{t.work.back}</Link>
        </div>
      </main>
    );
  }

  const images = project.gallery?.filter(Boolean).length ? project.gallery.filter(Boolean) : [project.image];

  return (
    <main data-testid="project-page">
      <Navbar />
      <div className="bg-[#F7F3EC] pt-28 lg:pt-40 pb-20 lg:pb-28">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <Link to="/#work" data-testid="project-back-link"
            className="inline-flex items-center gap-2 text-sm text-[#8A8378] hover:text-[#A6853B] transition-colors">
            <ArrowLeft size={15} /> {t.work.back}
          </Link>
          <div className="mt-8 mb-5"><p className="eyebrow-label">{t.work.eyebrow}</p></div>
          <h1 className="font-serif text-[#191713] text-4xl sm:text-5xl lg:text-6xl" data-testid="project-title">{project.title}</h1>
          <p className="mt-3 text-sm tracking-[0.16em] uppercase text-[#A6853B]">{L(project.industry)}</p>
          <p className="mt-6 text-lg text-[#191713]/70 leading-relaxed max-w-2xl">{L(project.description)}</p>
          <p className="mt-3 text-sm text-[#8A8378]">{L(project.services_provided)}</p>
          {project.link && (
            <a href={project.link} target="_blank" rel="noreferrer" data-testid="project-visit-link"
              className="mt-6 inline-flex items-center gap-2 text-sm text-[#A6853B] hover:text-[#191713] transition-colors tracking-wide">
              {t.work.visit} <ArrowUpRight size={16} />
            </a>
          )}

          <div className="mt-12 space-y-8" data-testid="project-gallery">
            {images.map((img, i) => (
              <img key={i} src={img} alt={`${project.title} — ${i + 1}`} loading={i === 0 ? "eager" : "lazy"}
                className="w-full h-auto border border-[#191713]/10 bg-white" />
            ))}
          </div>

          <div className="mt-16 bg-[#191713] px-8 py-12 lg:px-14 text-center">
            <h2 className="font-serif text-2xl sm:text-3xl text-[#F7F3EC]">{t.finalCta.title}</h2>
            <Link to="/contact" data-testid="project-cta-contact" className="btn-gold mt-7">
              {t.finalCta.cta1} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
}
